import {
  type Cell,
  type CellContext,
  type FilterFn,
  type Column as TanstackColumn,
  type ColumnDef,
  type HeaderContext,
  columnFilteringFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createFilteredRowModel,
  createExpandedRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  type Row,
  type RowData,
  rowExpandingFeature,
  rowPaginationFeature,
  rowPinningFeature,
  rowSortingFeature,
  type ReactTable,
  type Table,
  tableFeatures,
} from "@tanstack/react-table";

import {
  BUILT_IN_SORT_FNS,
  enumIn,
  enumInArray,
  textWithOperator,
} from "./filter-fns";

/**
 * The one feature set every DataTable runs on.
 *
 * v9 no longer bundles every feature into each table — a feature has to be
 * registered here for its options, state and methods to exist at all. Anything
 * DataTable doesn't offer (grouping, faceting, global filtering, column
 * ordering/resizing, TanStack's own row selection) is deliberately left out;
 * selection lives in the MobX model instead, mirrored into row pinning.
 * Expanding is registered for every table but inert without `getSubRows`, so
 * only tables that pass it grow sub-rows.
 *
 * Registering the row models, filter fns and sort fns here rather than per
 * table also makes their keys the only valid `filterFn`/`sortFn` strings on a
 * column, which is what used to need hand-written module augmentation.
 */
export const dataTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnPinningFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowPinningFeature,
  rowSortingFeature,
  expandedRowModel: createExpandedRowModel(),
  filteredRowModel: createFilteredRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortedRowModel: createSortedRowModel(),
  filterFns: { enumIn, enumInArray, textWithOperator },
  // The same map `sortingFnForType` picks a name from, so the names a column can
  // ask for and the fns the table knows can't drift apart.
  sortFns: BUILT_IN_SORT_FNS,
});

export type DataTableFeatures = typeof dataTableFeatures;

/**
 * v9 threads the feature set through every table type, so these aliases save
 * every consumer from repeating it (`Table<DataTableFeatures, T>`).
 *
 * They intersect `T & RowData` rather than constraining `<T extends RowData>`:
 * v9 narrowed the row generic to `Record<string, any> | Array<any>`, which
 * DataTable's own `<T>` / `<T extends object>` generics don't satisfy, and
 * constraining here would cascade that requirement through `DataTableProps`
 * into every table in the app. The intersection is still just `T` for property
 * access on a row.
 */
export type DataTableInstance<T> = Table<DataTableFeatures, T & RowData>;

/**
 * What `useTable` actually returns: the core table plus React's own additions
 * (`state`, `FlexRender`, `Subscribe`). Only the pieces that read table state
 * need this — TanStack hands the plain `DataTableInstance` to header and cell
 * renderers, so shared props take that broader type instead.
 */
export type DataTableReactInstance<T> = ReactTable<
  DataTableFeatures,
  T & RowData
>;

export type DataTableColumnDef<T> = ColumnDef<
  DataTableFeatures,
  T & RowData,
  unknown
>;

export type DataTableColumn<T> = TanstackColumn<
  DataTableFeatures,
  T & RowData,
  unknown
>;

export type DataTableRow<T> = Row<DataTableFeatures, T & RowData>;

export type DataTableCell<T> = Cell<DataTableFeatures, T & RowData, unknown>;

/** Props of a column's `header` renderer. */
export type DataTableHeaderContext<T> = HeaderContext<
  DataTableFeatures,
  T & RowData,
  unknown
>;

/** Props of a column's `cell` renderer. */
export type DataTableCellContext<T> = CellContext<
  DataTableFeatures,
  T & RowData,
  unknown
>;

/** A column's `filterFn` when it isn't one of the registered string keys. */
export type DataTableFilterFn<T> = FilterFn<DataTableFeatures, T & RowData>;
